import Button from '@atlaskit/button';
import { AkAddCircleIcon } from '@fuegokit/fuegoicons-react';
import { Box, Text } from '@fuegokit/react';
import React, { useCallback, useMemo, useState } from 'react';

import { usePersonalConfiguration } from '../../screens/PersonalConfigurationProvider';
import { useTogglDataContext } from '../../screens/TogglDataProvider';
import { getUserDashboardConfigWithUpdateWorkspace } from '../../services/personal-config';
import { WorkspaceWithProjects } from '../../types';
import { SwitchElement } from '../reusable/SwitchElement';
import { WorkspaceAndProjectsSelectors } from './WorkspaceAndProjectsSelectors';

export function TrackingStatusForm() {
  const { config, updateConfig } = usePersonalConfiguration();
  const { default_workspace_id, workspaces } = useTogglDataContext();
  const [isAddingNewWorkspace, setIsAddingNewWorkspace] = useState(false);

  const workspacesOptions = useMemo(
    () =>
      workspaces.map((workspace) => ({
        label: workspace.name,
        value: workspace.id,
      })),
    [workspaces]
  );
  const workspacesIds = useMemo(
    () => workspaces.map((w) => w.id),
    [workspaces]
  );

  const selectedWorkspaces = useMemo<WorkspaceWithProjects[]>(() => {
    if (config.userDashboard.workspaces.length) {
      return config.userDashboard.workspaces.filter((w) =>
        workspacesIds.includes(w.id)
      );
    }
    const defaultWorkspace = workspaces.find(
      (w) => w.id === default_workspace_id
    );
    return defaultWorkspace
      ? [{ id: defaultWorkspace.id, allowedProjectIds: [] }]
      : [];
  }, [
    config.userDashboard.workspaces,
    workspaces,
    default_workspace_id,
    workspacesIds,
  ]);

  const displayAddNewWorkspaceSelect = useMemo(
    () => !selectedWorkspaces.length || isAddingNewWorkspace,
    [isAddingNewWorkspace, selectedWorkspaces.length]
  );

  const selectedWorkspacesIds = useMemo(
    () => selectedWorkspaces.map((w) => w.id),
    [selectedWorkspaces]
  );

  const isSharingStatus = useMemo(
    () =>
      config.userDashboard.isSharing ??
      Boolean(config.userDashboard.workspaces.length),
    [config.userDashboard.isSharing, config.userDashboard.workspaces.length]
  );

  const onChange = useCallback(
    (
      newConfig: WorkspaceWithProjects,
      index: number | undefined = undefined
    ) => {
      void updateConfig(
        getUserDashboardConfigWithUpdateWorkspace(
          config.userDashboard,
          newConfig
        )
      );
      if (!index) setIsAddingNewWorkspace(false);
    },
    [config]
  );

  const unselectedWorkspacesNumber =
    workspacesOptions.length - selectedWorkspacesIds.length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2',
      }}
    >
      <SwitchElement
        label="Share tracking status"
        value={isSharingStatus}
        id="shareTrackingStatus"
        onToggle={() =>
          void updateConfig({
            userDashboard: {
              workspaces: !config.userDashboard.workspaces.length
                ? [{ id: default_workspace_id, allowedProjectIds: [] }]
                : config.userDashboard.workspaces,
              isSharing: !config.userDashboard.isSharing,
            },
          })
        }
        labelStyles={{
          fontWeight: 'bold',
          fontSize: '4',
        }}
      />
      <Text color="text.default">
        Select Toggl workspaces or specific Toggl projects to share tracking
        status with other Jira instance users.
      </Text>
      {selectedWorkspaces.map((workspace, index) => (
        <WorkspaceAndProjectsSelectors
          workspacesOptions={workspacesOptions}
          selectedWorkspaces={selectedWorkspacesIds}
          selectedWorkspaceOption={workspacesOptions.find(
            (w) => w.value == workspace.id
          )}
          allowedProjectIds={workspace.allowedProjectIds}
          key={workspace.id}
          isDisabled={!isSharingStatus}
          onChange={(newConfig: WorkspaceWithProjects) =>
            onChange(newConfig, index)
          }
          setIsAddingNewWorkspace={setIsAddingNewWorkspace}
        />
      ))}
      {displayAddNewWorkspaceSelect && (
        <WorkspaceAndProjectsSelectors
          workspacesOptions={workspacesOptions}
          selectedWorkspaces={selectedWorkspacesIds}
          allowedProjectIds={[]}
          key="new-workspace"
          isDisabled={!isSharingStatus}
          onChange={onChange}
          setIsAddingNewWorkspace={setIsAddingNewWorkspace}
        />
      )}
      <Box width="160px">
        {!displayAddNewWorkspaceSelect && unselectedWorkspacesNumber ? (
          <Button
            appearance="subtle"
            iconBefore={<AkAddCircleIcon />}
            shouldFitContainer={true}
            onClick={() => setIsAddingNewWorkspace(true)}
            isDisabled={!isSharingStatus}
          >
            Add workspace
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
